import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight, CornerDownLeft, Mic, MicOff, Search, Sparkles } from 'lucide-react';
import type { EntityPreview } from '@nw/contracts';
import { cn } from '@nw/ui';
import { rankCommands, toast, useSearch, type AssistantCommand, type AssistantContext, type AssistantReply } from '@nw/web-platform';
import { speak, useSpeech } from '../voice.js';

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

type Row = { kind: 'command'; c: AssistantCommand; arg?: string; score: number } | { kind: 'entity'; e: EntityPreview };

/**
 * The assistant: one line for intent, typed or spoken. Commands come from the shell and every
 * enabled module; below them, matching things from your world. Enter runs the best match.
 */
export function AssistantCore({
  commands,
  ctx,
  variant,
  autoFocus,
  onDone,
  initial = '',
  idle,
  autoListen,
}: {
  commands: AssistantCommand[];
  ctx: Omit<AssistantContext, 'speak'>;
  variant: 'overlay';
  autoFocus?: boolean;
  onDone?: () => void;
  initial?: string;
  /** Shown under the line while nothing is typed (quick actions, places). */
  idle?: React.ReactNode;
  /** Start listening as soon as it opens (a held Ask orb). */
  autoListen?: boolean;
}) {
  const [text, setText] = useState(initial);
  const [reply, setReply] = useState<AssistantReply | null>(null);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useDebounced(text, 150);
  const search = useSearch(q.length >= 2 ? q : '');

  const rows: Row[] = useMemo(() => {
    const cmds = rankCommands(commands, text)
      .slice(0, 5)
      .map((x) => ({
        kind: 'command' as const,
        c: x.c,
        arg: x.m.arg,
        score: x.m.score,
      }));
    const ents = (search.data?.items ?? []).slice(0, 8).map((e) => ({ kind: 'entity' as const, e }));
    const strong = cmds.filter((c) => c.score >= 0.9);
    return [...strong, ...ents, ...cmds.filter((c) => c.score < 0.9)];
  }, [commands, text, search.data]);

  useEffect(() => setActive(0), [text]);

  const runRow = async (row: Row | undefined, spoken: boolean) => {
    if (!row) return;
    if (row.kind === 'entity') {
      void navigate({ to: row.e.href });
      onDone?.();
      return;
    }
    if (row.c.needsArg && !row.arg) {
      setText(`${row.c.usage.split(' <')[0]} `);
      inputRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      const r = await row.c.run(row.arg, { ...ctx, speak });
      setReply(r);
      if (spoken) speak(r.message);
      if (r.ok) {
        setText('');
        void qc.invalidateQueries();
        if (r.href && r.open === 'focus') ctx.navigate(r.href);
        if (variant === 'overlay' && (r.open || !r.href)) onDone?.();
        else if (r.href && !r.open)
          toast.success(r.message, {
            label: 'Open',
            run: () => ctx.navigate(r.href!),
          });
      }
    } finally {
      setBusy(false);
    }
  };

  const voice = useSpeech((finalText) => {
    setText(finalText);
    const best = rankCommands(commands, finalText)[0];
    if (best && best.m.score >= 0.9) void runRow({ kind: 'command', c: best.c, arg: best.m.arg, score: best.m.score }, true);
  });
  const shown = voice.listening && voice.interim ? voice.interim : text;
  useEffect(() => {
    if (autoListen && voice.supported) voice.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoListen]);

  const examples = ['add book Dune', 'note Rivers remember what cities forget', 'open mind', 'atmosphere deep'];

  return (
    <div className={'nw-assistant nw-assistant-overlay'}>
      <form
        className="nw-assistant-line"
        onSubmit={(e) => {
          e.preventDefault();
          void runRow(rows[active], false);
        }}
      >
        <Sparkles className="size-5 shrink-0 text-[var(--nw-river)]" aria-hidden />
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          value={shown}
          onChange={(e) => {
            setText(e.target.value);
            setReply(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((a) => Math.min(rows.length - 1, a + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(0, a - 1));
            }
          }}
          placeholder={voice.listening ? 'Listening…' : 'Ask or command. Try “add book Dune” or “open mind”'}
          aria-label="Ask or command"
          role="combobox"
          aria-expanded={rows.length > 0}
          aria-controls={`nw-assistant-results-${variant}`}
          aria-activedescendant={rows[active] ? `nw-as-${variant}-${active}` : undefined}
          className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-[var(--nw-quiet)]"
        />
        {text ? (
          <button type="submit" disabled={busy} className="nw-icon-btn" aria-label="Run">
            <CornerDownLeft className="size-4" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => (voice.listening ? voice.stop() : voice.start())}
          disabled={!voice.supported}
          title={voice.supported ? (voice.listening ? 'Stop listening' : 'Speak a command') : 'This browser does not offer speech recognition'}
          aria-label={voice.listening ? 'Stop listening' : 'Speak a command'}
          aria-pressed={voice.listening}
          className={cn('nw-mic', voice.listening && 'nw-mic-live')}
        >
          {voice.supported ? <Mic className="size-5" /> : <MicOff className="size-5" />}
        </button>
      </form>
      {voice.error ? <p className="mt-2 px-2 text-sm text-[var(--nw-danger)]">{voice.error}</p> : null}
      {reply ? (
        <p role="status" className={cn('mt-3 flex items-center gap-2 px-2 text-sm', reply.ok ? 'text-[var(--nw-ink)]' : 'text-[var(--nw-danger)]')}>
          <span className={cn('size-2 rounded-full', reply.ok ? 'bg-[var(--nw-live)]' : 'bg-[var(--nw-danger)]')} />
          {reply.message}
          {reply.ok && reply.href ? (
            <button type="button" className="font-semibold underline-offset-4 hover:underline" onClick={() => (ctx.navigate(reply.href!), onDone?.())}>
              Open
            </button>
          ) : null}
        </p>
      ) : null}
      {text.trim() ? (
        <ul id={`nw-assistant-results-${variant}`} role="listbox" aria-label="Suggestions" className="nw-assistant-results">
          {rows.map((r, i) => (
            <li
              key={r.kind === 'command' ? r.c.id : r.e.key}
              id={`nw-as-${variant}-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseEnter={() => setActive(i)}
              onClick={() => void runRow(r, false)}
              className={cn('nw-assistant-row', i === active && 'nw-assistant-row-active')}
            >
              {r.kind === 'command' ? (
                <>
                  <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--nw-primary)] text-[var(--nw-on-primary)]">{r.c.icon ? <r.c.icon className="size-4" /> : <Sparkles className="size-4" />}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold">
                      {r.c.title}
                      {r.arg ? <span className="font-normal text-[var(--nw-quiet)]"> “{r.arg}”</span> : null}
                    </span>
                    <span className="block text-xs text-[var(--nw-quiet)]">{r.c.usage}</span>
                  </span>
                </>
              ) : (
                <>
                  <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--nw-glass-strong)]">
                    <Search className="size-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{r.e.title}</span>
                    <span className="block text-xs text-[var(--nw-quiet)]">{r.e.typeLabel}</span>
                  </span>
                  <ArrowUpRight className="size-4 text-[var(--nw-quiet)]" />
                </>
              )}
            </li>
          ))}
          {rows.length === 0 && !search.isFetching ? <li className="px-4 py-3 text-sm text-[var(--nw-quiet)]">No command or item matches “{text}”.</li> : null}
        </ul>
      ) : idle ? (
        idle
      ) : (
        <div className="mt-4 flex flex-wrap gap-2 px-1" aria-label="Examples">
          {examples.map((ex) => (
            <button key={ex} type="button" onClick={() => (setText(ex), inputRef.current?.focus())} className="nw-glass-pill h-9 px-3.5 text-[13px]">
              {ex}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
